// src/modules/requerimiento/components/RequerimientoListado.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Typography,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Chip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";
import ServicioAvatar from "../../../components/ServicioAvatar";

// services
import {
  getReqSolicitudes,
  getAdicionAsignados,
  updateRequerimiento,
} from "../services/reqSolicitudes";
import { getProfile } from "../services/auth";

// === HELPER: Formato de fecha ===
const fmt = (v) => {
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("es-BO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

// === HELPER: Estilos para el código según el estado ===
const getCodStyles = (estado) => {
  const isFinalizado = Number(estado) === 7;
  return {
    fontWeight: isFinalizado ? "bold" : "normal",
    color: isFinalizado ? "green" : "inherit",
  };
};

const compactFieldSx = {
  "& .MuiInputBase-root": { height: 34, fontSize: "0.82rem" },
  "& .MuiInputLabel-root": { fontSize: "0.82rem" },
  "& .MuiButton-root": { minHeight: 34 },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const truncate = (value, max = 55) => {
  const text = String(value || "-");
  return text.length > max ? `${text.slice(0, max)}...` : text;
};

const TruncatedCell = ({ value, max = 55, width = 180 }) => {
  const text = String(value || "-");
  return (
    <Tooltip title={text} arrow>
      <Box
        component="span"
        sx={{
          display: "block",
          maxWidth: width,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {truncate(text, max)}
      </Box>
    </Tooltip>
  );
};

const stickyActionsSx = {
  position: "sticky",
  right: 0,
  zIndex: 2,
  backgroundColor: "background.paper",
  boxShadow: "-6px 0 8px -8px rgba(0,0,0,0.35)",
};

export default function RequerimientoListado({ onOpenDetalle }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const headerBg = theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100];

  // listado
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [errorList, setErrorList] = useState("");
  const [buscar, setBuscar] = useState("");

  // server params
  const [sucursal, setSucursal] = useState("");
  const [pendiente, setPendiente] = useState("true");

  // paginación (solo front)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // dialog eliminar
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loadingModems, setLoadingModems] = useState(false);
  const [modemsAsignados, setModemsAsignados] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [obsFin, setObsFin] = useState("");
  const [username, setUsername] = useState("");

  // cargar sucursal del perfil
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const user = await getProfile();
        if (!mounted) return;
        const s = user?.sucursal || "La Paz";
        setUsername(user?.username || "usuario");
        setSucursal(s);
      } catch {
        setSucursal("La Paz");
        setUsername("usuario");
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Consulta al backend (ya NO usa limit ni buscar)
  const fetchListado = async ({
    page: pageArg = page,
    pageSize: pageSizeArg = rowsPerPage,
    suc: sucArg = sucursal,
    pend: pendArg = pendiente,
    search: searchArg = buscar,
  } = {}) => {
    if (!sucArg) return;
  
    setLoadingList(true);
    setErrorList("");
  
    try {
      const params = {
        page: pageArg + 1,          // backend 1-based
        pageSize: pageSizeArg,
        sucursal: sucArg,
        pendiente: pendArg,
      };
  
      if (searchArg && searchArg.trim()) {
        params.buscar = searchArg.trim();
      }
  
      const { data } = await getReqSolicitudes(params);
  
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotalRows(Number(data?.total) || 0);
      setPage(pageArg);
      setRowsPerPage(pageSizeArg);
    } catch (e) {
      console.error(e);
      setErrorList("No se pudo cargar el listado de requerimientos.");
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoadingList(false);
    }
  };

  // Cargar datos iniciales y cuando cambie sucursal/pendiente
  useEffect(() => {
    if (!sucursal) return;
    fetchListado({ page: 0, search: buscar });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sucursal, pendiente]);

  const handleOpenDelete = async (row) => {
    setDeleteTarget(row);
    setDeleteOpen(true);
    setDeleteError("");
    setModemsAsignados([]);
    setObsFin("");
    setLoadingModems(true);

    try {
      const { data } = await getAdicionAsignados(row.cod);
      setModemsAsignados(Array.isArray(data) ? data : []);
    } catch (e) {
      setDeleteError("No se pudo verificar los equipos asignados.");
      setModemsAsignados([]);
    } finally {
      setLoadingModems(false);
    }
  };

  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setDeleteTarget(null);
    setModemsAsignados([]);
    setDeleteError("");
    setDeleting(false);
    setObsFin("");
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await updateRequerimiento(deleteTarget.cod, {
        estado: 0,
        obs_fin:
          obsFin + ". Requerimiento eliminado por " + username ||
          "Requerimiento eliminado por " + username,
      });
      await fetchListado();
      handleCloseDelete();
    } catch (e) {
      setDeleteError("No se pudo eliminar el requerimiento.");
    } finally {
      setDeleting(false);
    }
  };

  const tieneModems = modemsAsignados && modemsAsignados.length > 0;

  // === HELPER: RENDERIZAR ACCIONES ===
  const renderAcciones = (r) => {
    const estadoNum = Number(r.estado);
    const isFinalizado = Number(r.estado) === 7;
    const showDelete = ![0, 5, 6, 8].includes(estadoNum);

    return (
      <Box sx={{ whiteSpace: "nowrap", display: "flex", justifyContent: "center", gap: 0.25 }}>
        <Tooltip title={isFinalizado ? "Requerimiento finalizado" : "Ver"}>
          <IconButton
            size="small"
            onClick={() => onOpenDetalle ? onOpenDetalle(r) : navigate(`/requerimiento/${r.cod}`)}
            sx={{ p: 0.35 }}
            color={isFinalizado ? "success" : "default"}
          >
            {isFinalizado ? <CheckCircleIcon /> : <VisibilityIcon />}
          </IconButton>
        </Tooltip>

        {showDelete && ( // 👈 solo muestra eliminar cuando corresponde
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              sx={{ p: 0.35 }}
              onClick={() => handleOpenDelete(r)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* === BARRA DE FILTROS Y ACCIONES === */}
      <Grid
        container
        spacing={1}
        alignItems="center"
        sx={{
          mb: 1.25,
          "& .MuiInputBase-root": { height: 34, fontSize: "0.82rem" },
          "& .MuiInputLabel-root": { fontSize: "0.82rem" },
          "& .MuiButton-root": { minHeight: 34, fontSize: "0.78rem" },
        }}
      >
        {/* Columna 1: Sucursal */}
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <FormControl size="small" fullWidth sx={compactFieldSx}>
            <InputLabel id="sucursal-label">Sucursal</InputLabel>
            <Select
              labelId="sucursal-label"
              label="Sucursal"
              size="small"
              value={sucursal}
              onChange={(e) => setSucursal(e.target.value)}
            >
              <MenuItem value="La Paz">La Paz</MenuItem>
              <MenuItem value="Cochabamba">Cochabamba</MenuItem>
              <MenuItem value="Santa Cruz">Santa Cruz</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Columna 2: Buscar (solo filtra en memoria) */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            size="small"
            label="Buscar (cod, razón, tipo, usuario, técnicos, obs)"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            fullWidth
            sx={compactFieldSx}
          />
        </Grid>

        {/* Columna 3: Pendientes / Todos */}
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <FormControl size="small" fullWidth sx={compactFieldSx}>
            <InputLabel id="pendiente-label">Estado</InputLabel>
            <Select
              labelId="pendiente-label"
              label="Estado"
              size="small"
              value={pendiente}
              onChange={(e) => setPendiente(e.target.value)}
            >
              <MenuItem value="true">Pendientes</MenuItem>
              <MenuItem value="false">Todos</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Columna 4: Consultar (antes Recargar) */}
        <Grid size={{ xs: 6, sm: 3, md: 2 }}>
          <Button
            variant="contained"
            disabled={loadingList}
            onClick={() => fetchListado({ page: 0, search: buscar })}
            fullWidth
          >
            {loadingList ? "Cargando..." : "Consultar"}
          </Button>
        </Grid>
      </Grid>
      {/* === FIN BARRA DE FILTROS === */}

      {loadingList ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={20} />
          <Typography>Cargando requerimientos…</Typography>
        </Stack>
      ) : errorList ? (
        <Alert severity="error">{errorList}</Alert>
      ) : rows.length === 0 ? (
        <Alert severity="info">No hay datos para mostrar.</Alert>
      ) : (
        <>
          {/* === RENDERIZADO CONDICIONAL === */}
          {isMobile ? (
            // 1. VISTA DE TARJETAS (Móvil / Pantalla Pequeña)
            <Grid container spacing={1}>
              {rows.map((r) => {
                const isFinalizado = Number(r.estado) === 7;
                return (
                  <Grid size={{ xs:12 }} key={r.cod}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderLeft: `5px solid ${
                          isFinalizado
                            ? theme.palette.success.main
                            : theme.palette.primary.main
                        }`,
                      }}
                    >
                      <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                        {/* Título, Tipo y Acciones */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 0.75,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            component="div"
                            sx={{ ...getCodStyles(r.estado), fontSize: "0.95rem" }}
                          >
                            #{r.cod}
                          </Typography>
                          <Chip
                            label={r.nombre_ot_tipo || "N/A"}
                            color={isFinalizado ? "success" : "primary"}
                            size="small"
                            sx={{ fontWeight: "bold", height: 20, fontSize: "0.68rem" }}
                          />
                          <Box sx={{ flexShrink: 0 }}>{renderAcciones(r)}</Box>
                        </Box>

                        {/* Detalles */}
                        <Typography variant="body2" sx={{ mb: 0.25, fontSize: "0.78rem" }}>
                          <Typography component="span" fontWeight="bold">
                            Cliente:
                          </Typography>{" "}
                          <ServicioAvatar imagen={r.imagen_principal} nombre={r.razon_social} size={20} sx={{ display: "inline-flex", verticalAlign: "middle", mr: 0.5 }} />
                          {r.razon_social || "-"} ({r.usu || "-"})
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.25, fontSize: "0.78rem" }}>
                          <Typography component="span" fontWeight="bold">
                            Técnicos:
                          </Typography>{" "}
                          {r.tecnico || "-"}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.25, fontSize: "0.78rem" }}>
                          <Typography component="span" fontWeight="bold">
                            Fecha Coord.:
                          </Typography>{" "}
                          {fmt(r.fecha_coordinacion)} {r.hora_coordinacion}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.25, fontSize: "0.78rem" }}>
                          <Typography component="span" fontWeight="bold">
                            Ubicación:
                          </Typography>{" "}
                          {r.sucursal || "-"} · Placa(s): {r.placa || "-"}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mt: 0.5, whiteSpace: "pre-line", fontSize: "0.78rem" }}
                        >
                          <Typography component="span" fontWeight="bold">
                            Descripción:
                          </Typography>{" "}
                          {truncate(r.descripcion, 90)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mt: 0.5, whiteSpace: "pre-line", fontSize: "0.78rem" }}
                        >
                          <Typography component="span" fontWeight="bold">
                            Obs:
                          </Typography>{" "}
                          {truncate(r.observacion, 90)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            // 2. VISTA DE TABLA (Desktop / Pantalla Grande)
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
              <Table
                size="small"
                sx={{
                  tableLayout: "fixed",
                  "& .MuiTableCell-root": {
                    px: 0.65,
                    py: 0.4,
                    fontSize: "0.76rem",
                    lineHeight: 1.2,
                  },
                  "& .MuiTableCell-head": {
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    color: theme.palette.text.primary,
                    backgroundColor: headerBg,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 54 }}>#</TableCell>
                    <TableCell sx={{ width: 92 }}>Tipo</TableCell>
                    <TableCell sx={{ width: 155 }}>
                      Razón social
                    </TableCell>
                    <TableCell sx={{ width: 190 }}>
                      Descripción
                    </TableCell>
                    <TableCell sx={{ width: 82 }}>Sucursal</TableCell>
                    <TableCell sx={{ width: 140 }}>
                      Placa(s)
                    </TableCell>
                    <TableCell sx={{ width: 82 }}>Emitido</TableCell>
                    <TableCell sx={{ width: 190 }}>
                      Técnicos
                    </TableCell>
                    <TableCell sx={{ width: 125 }}>Fecha Coord.</TableCell>
                    <TableCell sx={{ width: 160 }}>
                      Observación
                    </TableCell>
                    <TableCell sx={{ width: 118 }}>
                      Registro
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        ...stickyActionsSx,
                        width: 72,
                        zIndex: 3,
                        backgroundColor: headerBg,
                      }}
                    >
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow
                      key={r.cod}
                      hover
                      sx={
                        Number(r.estado) === 0
                          ? {
                              backgroundColor: theme.palette.error.light,
                            }
                          : {}
                      }
                    >
                      <TableCell sx={getCodStyles(r.estado)}>
                        {r.cod}
                      </TableCell>
                      <TableCell><TruncatedCell value={r.nombre_ot_tipo} max={18} width={82} /></TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <ServicioAvatar imagen={r.imagen_principal} nombre={r.razon_social} size={24} />
                          <TruncatedCell value={r.razon_social} max={32} width={120} />
                        </Box>
                      </TableCell>
                      <TableCell><TruncatedCell value={r.descripcion} max={45} width={180} /></TableCell>
                      <TableCell>{r.sucursal || "-"}</TableCell>
                      <TableCell sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{r.placa || "-"}</TableCell>
                      <TableCell>{r.usu || "-"}</TableCell>
                      <TableCell sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{r.tecnico || "-"}</TableCell>
                      <TableCell>
                        {fmt(r.fecha_coordinacion)} {r.hora_coordinacion}
                      </TableCell>
                      <TableCell><TruncatedCell value={r.observacion} max={35} width={150} /></TableCell>
                      <TableCell>
                        {fmt(r.fecha)} {r.hora_registro}
                      </TableCell>
                      <TableCell align="center" sx={stickyActionsSx}>
                        {renderAcciones(r)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Paginación (solo front) */}
          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            onPageChange={(_, newPage) => {
              // Cambiar de página => nueva consulta al backend
              fetchListado({
                page: newPage,
                pageSize: rowsPerPage,
                search: buscar,
              });
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10) || 50;
              // Cambiar tamaño de página => vamos a la página 0
              fetchListado({
                page: 0,
                pageSize: newSize,
                search: buscar,
              });
            }}
            rowsPerPageOptions={[50, 100, 200]}
            sx={{
              minHeight: 40,
              "& .MuiTablePagination-toolbar": { minHeight: 40, px: 1 },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "0.78rem",
              },
            }}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} de ${count}`
            }
          />
        </>
      )}

      {/* Dialog eliminar */}
      <Dialog
        open={deleteOpen}
        onClose={deleting ? null : handleCloseDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Eliminar requerimiento
          {deleteTarget ? ` #${deleteTarget.cod}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {loadingModems ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography>Verificando módems asignados…</Typography>
            </Stack>
          ) : (
            <>
              {deleteError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {deleteError}
                </Alert>
              )}

              {tieneModems ? (
                <>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Este requerimiento tiene módems adicionados. No se puede
                    eliminar.
                  </Alert>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    Módems adicionados:
                  </Typography>
                  <List dense>
                    {modemsAsignados.map((m) => (
                      <ListItem key={m.id_historial} disableGutters>
                        <ListItemText
                          primary={`${m.num_sim} · ${m.modelo || ""}`}
                          secondary={`IMEI: ${
                            m.emai || "-"
                          } · Estado: ${m.estado || "-"}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    ¿Seguro que deseas eliminar este requerimiento?.
                  </Alert>
                  <TextField
                    label="Observación de eliminación"
                    value={obsFin}
                    onChange={(e) => setObsFin(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Ej: Se anuló porque el cliente canceló el servicio."
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={deleting}>
            Cerrar
          </Button>
          {!loadingModems && !tieneModems && (
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={deleting || obsFin.trim().length === 0}
            >
              {deleting ? "Eliminando..." : "Eliminar"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
